import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  parseStorefrontSiteSettings,
  siteSettingsHasPlaceholders,
  STOREFRONT_SITE_SETTINGS_KEY,
  STOREFRONT_LEAD_MAGNET_SLUGS,
  type StorefrontLeadMagnetsStatus,
} from '@wapharma/shared';
import { unwrapData } from '@wapharma/shared';
import { apiGet } from '../api';
import { apiGetListSimple } from '../apiList';
import { getToken } from '../auth';
import { getDisplayError } from '../apiErrors';

export type LaunchCheckId =
  | 'legal'
  | 'shipping'
  | 'payment'
  | 'featured'
  | 'flows'
  | 'campaigns'
  | 'coupons';

export type LaunchCheck = {
  id: LaunchCheckId;
  label: string;
  ok: boolean;
  detail?: string;
  optional?: boolean;
};

export const LAUNCH_BLOCKING_IDS: readonly LaunchCheckId[] = [
  'legal',
  'shipping',
  'payment',
  'featured',
  'flows',
] as const;

export function useLaunchReadiness() {
  const token = getToken();
  const [checks, setChecks] = useState<LaunchCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const next: LaunchCheck[] = [];
    try {
      const [siteRes, shipping, payment, featured, leadStatus, campaigns, coupons] =
        await Promise.all([
          token
            ? apiGet<{ data: { value: string } | null }>(
                `/admin/settings/${encodeURIComponent(STOREFRONT_SITE_SETTINGS_KEY)}`,
                token,
              ).then((r) => unwrapData(r))
            : Promise.resolve(null),
          token
            ? apiGetListSimple<{ active?: boolean }>('/fulfillment/shipping-methods', token)
            : Promise.resolve([]),
          token
            ? apiGetListSimple<{ active?: boolean }>('/fulfillment/payment-methods', token)
            : Promise.resolve([]),
          apiGet<{ data: unknown[] }>('/catalog/products?featured=true&limit=1').then(
            (r) => (r as { data?: unknown[] }).data ?? [],
          ),
          token
            ? apiGet<{ data: StorefrontLeadMagnetsStatus }>(
                '/lead-flows/storefront-status',
                token,
              )
                .then((r) => r.data ?? null)
                .catch(() => null)
            : Promise.resolve(null),
          token ? apiGetListSimple<unknown>('/catalog/admin/campaigns', token) : Promise.resolve([]),
          token
            ? apiGetListSimple<{ isActive?: boolean }>('/sales/discount-codes', token)
            : Promise.resolve([]),
        ]);

      const site = parseStorefrontSiteSettings(
        siteRes && typeof siteRes === 'object' && 'value' in siteRes
          ? String(siteRes.value ?? '')
          : '',
      );
      const siteIssues = siteSettingsHasPlaceholders(site);
      next.push({
        id: 'legal',
        label: 'Identité légale sans placeholder',
        ok: siteIssues.length === 0,
        detail: siteIssues.join(' · ') || undefined,
      });

      const activeShipping = shipping.filter((m) => m.active !== false);
      next.push({
        id: 'shipping',
        label: 'Au moins une méthode de livraison active',
        ok: activeShipping.length > 0,
        detail: `${activeShipping.length} active(s)`,
      });

      const activePayment = payment.filter((m) => m.active !== false);
      next.push({
        id: 'payment',
        label: 'Au moins un mode de paiement actif',
        ok: activePayment.length > 0,
        detail: `${activePayment.length} actif(s)`,
      });

      next.push({
        id: 'featured',
        label: 'Produits à la une publiés',
        ok: Array.isArray(featured) && featured.length > 0,
      });

      const magnetItems = leadStatus
        ? STOREFRONT_LEAD_MAGNET_SLUGS.map((slug) => leadStatus[slug])
        : [];
      const magnetsWired = magnetItems.every(
        (m) => m && (m.status === 'ready' || m.status === 'blocked'),
      );
      const magnetsReady = magnetItems.filter((m) => m?.status === 'ready').length;
      const magnetsBlocked = magnetItems.filter((m) => m?.status === 'blocked').length;
      next.push({
        id: 'flows',
        label: 'Parcours vitrine (routine + analyse photo)',
        ok: Boolean(leadStatus) && magnetsWired,
        detail: leadStatus
          ? `${magnetsReady} prêt(s)${magnetsBlocked ? ` · ${magnetsBlocked} matching vide` : ''}`
          : 'Statut indisponible',
      });

      next.push({
        id: 'campaigns',
        label: 'Campagnes configurées (optionnel)',
        ok: true,
        optional: true,
        detail: `${campaigns.length} campagne(s)`,
      });

      const activeCoupons = coupons.filter((c) => c.isActive !== false);
      next.push({
        id: 'coupons',
        label: 'Codes promo actifs (si promotions prévues)',
        ok: activeCoupons.length > 0 || coupons.length === 0,
        optional: true,
        detail: `${activeCoupons.length} actif(s)`,
      });
    } catch (e) {
      setError(getDisplayError(e));
    } finally {
      setChecks(next);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const byId = useMemo(() => {
    const map = new Map<LaunchCheckId, LaunchCheck>();
    for (const c of checks) map.set(c.id, c);
    return map;
  }, [checks]);

  const blocking = useMemo(
    () => checks.filter((c) => !c.ok && LAUNCH_BLOCKING_IDS.includes(c.id)),
    [checks],
  );
  const ready = blocking.length === 0 && checks.length > 0;

  const progressPercent = useMemo(() => {
    if (!checks.length) return 0;
    const done = checks.filter((c) => c.ok).length;
    return Math.round((done / checks.length) * 100);
  }, [checks]);

  return { checks, byId, loading, error, reload, ready, progressPercent };
}
